// Server-side draft persistence: a Redux middleware that autosaves the in-
// progress intake (area + product selections) for a verified visitor, and a
// thunk that rehydrates the form from a saved draft on resume.
//
// Drafts are best-effort resume state, never the source of truth — pricing and
// geometry are always recomputed/re-verified at submit — so saves are fire-and-
// forget and failures are swallowed. Only fields the server can safely re-trust
// are persisted; custom polygons can't be turned back into a signed token, so a
// custom-area resume restores the selection but asks the user to re-upload.
import { apiFetch } from '../api/public-client.js';
import {
  setAreaType, setAreaRefId, computeAreaStats, restoreCustomGeometry,
} from './area-slice.js';
import { setSelections, scheduleEstimate } from './selections-slice.js';

// Actions after which the draft is worth re-saving. setSelections is excluded
// so rehydration doesn't immediately echo the just-loaded draft back.
const SAVE_TRIGGERS = new Set([
  'area/setAreaType',
  'area/setAreaRefId',
  'area/computeStats/fulfilled',
  'area/uploadCustomGeometry/fulfilled',
  'area/restoreCustomGeometry/fulfilled',
  'selections/toggleProduct',
  'selections/toggleProductOption',
  'selections/clearSelections',
]);

const DEBOUNCE_MS = 1200;
let saveTimer = null;

// Autosave is paused until this timestamp. Used while a draft is being replayed
// (so loading it back in doesn't immediately echo-save it) and right after a
// discard (so a queued save can't recreate the row we just deleted).
let suppressUntil = 0;

const cancelPendingSave = () => {
  clearTimeout(saveTimer);
  saveTimer = null;
};

const suppressAutosave = (ms) => {
  cancelPendingSave();
  suppressUntil = Date.now() + ms;
};

// The display geometry arrives from the server as a JSON string (PostGIS
// ST_AsGeoJSON). Persist it as a parsed object so the JSONB column round-trips
// cleanly and the resume endpoint can re-validate it.
const toGeoJsonObject = (value) => {
  if (value && typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

const buildSnapshot = (state) => {
  const { areaType, areaRefId } = state.area;
  const stats = state.area.stats.data || {};
  const customGeoJson = areaType === 'custom'
    ? toGeoJsonObject(stats.displayGeoJson)
    : null;
  return {
    areaType,
    areaRefId: areaType === 'custom' ? null : areaRefId,
    areaSqMi: stats.areaSqMi ?? null,
    estPopulation: stats.estPopulation ?? null,
    customGeoJson,
    selections: state.selections.items,
  };
};

// Don't save empty drafts — an untouched form shouldn't create a "resume" row.
const hasContent = (snap) => Boolean(snap.areaRefId)
  || snap.areaType === 'custom'
  || snap.selections.length > 0;

export const draftAutosave = (store) => (next) => (action) => {
  const result = next(action);
  const type = action?.type;

  // "Start over" deletes the draft; cancel any queued save and stay quiet long
  // enough that the reset actions it dispatches can't schedule a new one.
  if (type === 'contact/discardDraft/pending') {
    suppressAutosave(DEBOUNCE_MS + 1000);
    return result;
  }

  if (SAVE_TRIGGERS.has(type)) {
    // Any new trigger supersedes a pending save, so a reset to an empty form
    // cancels an earlier save that captured a now-stale snapshot.
    cancelPendingSave();
    if (Date.now() < suppressUntil) return result;
    const state = store.getState();
    if (state.contact.verified) {
      const snapshot = buildSnapshot(state);
      if (hasContent(snapshot)) {
        saveTimer = setTimeout(() => {
          apiFetch('/api/intake/draft', { method: 'PUT', body: snapshot }).catch(() => {});
        }, DEBOUNCE_MS);
      }
    }
  }
  return result;
};

// Rehydrates the form from a saved draft. For county/city areas it recomputes a
// fresh geometry token (so pricing/submit work again); for a custom area it
// re-validates the saved boundary server-side to mint a fresh token, restoring
// the highlighted area, stats, and estimate.
export const hydrateDraft = (draft) => async (dispatch) => {
  if (!draft || !draft.areaType) return;

  // Pause autosave while we replay the draft so restoring it doesn't echo a
  // redundant save back to the server.
  suppressAutosave(60_000);
  try {
    dispatch(setAreaType(draft.areaType));

    const restoreSelections = (token) => {
      if (Array.isArray(draft.selections) && draft.selections.length) {
        dispatch(setSelections(draft.selections));
        if (token) dispatch(scheduleEstimate(token));
      }
    };

    if (draft.areaType === 'custom') {
      if (draft.customGeoJson) {
        const res = await dispatch(restoreCustomGeometry(draft.customGeoJson));
        restoreSelections(res?.payload?.geometryToken);
      } else if (Array.isArray(draft.selections) && draft.selections.length) {
        dispatch(setSelections(draft.selections));
      }
      return;
    }

    if (draft.areaRefId != null) {
      dispatch(setAreaRefId(draft.areaRefId));
      const res = await dispatch(computeAreaStats({
        areaType: draft.areaType,
        areaRefId: draft.areaRefId,
      }));
      restoreSelections(res?.payload?.geometryToken);
    }
  } finally {
    // Resume autosave once the replay (and its trailing reset effects) settle.
    suppressUntil = Date.now() + DEBOUNCE_MS;
  }
};
